import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getNotificationsApi, markNotificationAsReadApi } from '../api/user';

const Notifications = () => {
    const history = useHistory();
    const { auth } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [markingReadId, setMarkingReadId] = useState(null);
    const [actionError, setActionError] = useState(null);

    const fetchNotifications = useCallback(async (token) => {
        try {
            setLoading(true);
            setError(null);

            const result = await getNotificationsApi(token);
            console.log(result);
            if (result && result.docs) {
                setNotifications(result.docs);
            } else {
                setError('Failed to load notifications');
            }
        } catch (err) {
            setError('Error loading notifications');
            console.error('Error fetching notifications:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Redirect if not logged in
    useEffect(() => {
        // Only redirect if auth is explicitly null (not undefined during loading)
        if (auth === null) {
            history.push('/login');
            return;
        }
    }, [auth, history]);

    // Fetch notifications when user is authenticated
    useEffect(() => {
        if (auth && auth.idUser) {
            fetchNotifications(auth.token);
        }
    }, [auth, fetchNotifications]);

    const markNotificationAsRead = async (notificationId) => {
        if (markingReadId) {
            return;
        }

        try {
            setMarkingReadId(notificationId);
            const result = await markNotificationAsReadApi(notificationId, auth.token);

            if (result && result.doc) {
                setNotifications(prev =>
                    prev.map(notif =>
                        notif.id === notificationId
                            ? { ...notif, ...result.doc }
                            : notif
                    )
                );

                window.dispatchEvent(new CustomEvent('notificationRead', {
                    detail: { notificationId, updatedNotification: result.doc }
                }));
            } else {
                console.error('Failed to mark notification as read');
                setActionError(
                    result?.message ||
                    'No se pudo marcar la notificación como leída. Inténtalo de nuevo.'
                );
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
            setActionError('No se pudo marcar la notificación como leída. Inténtalo de nuevo.');
        } finally {
            setMarkingReadId(null);
        }
    };

    useEffect(() => {
        if (!actionError) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setActionError(null);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [actionError]);

    // Don't render anything while auth is loading
    if (auth === undefined) {
        return null;
    }

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'raffle_winner':
                return '🎉';
            case 'bid_update':
                return '💰';
            case 'order_status':
                return '📦';
            case 'system':
                return '🔔';
            default:
                return '📢';
        }
    };

    const getNotificationBadgeVariant = (type) => {
        switch (type) {
            case 'raffle_winner':
                return 'success';
            case 'bid_update':
                return 'info';
            case 'order_status':
                return 'primary';
            case 'system':
                return 'warning';
            default:
                return 'secondary';
        }
    };

    const getNotificationTypeTranslation = (type) => {
        switch (type) {
            case 'raffle_winner':
                return 'Ganador de Rifa';
            case 'bid_update':
                return 'Actualización de Puja';
            case 'order_status':
                return 'Estado del Pedido';
            case 'system':
                return 'Sistema';
            default:
                return 'Notificación';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="notifications-page">
                <div className="notifications-hero">
                    <Container>
                        <Row>
                            <Col>
                                <h1>Notificaciones</h1>
                            </Col>
                        </Row>
                    </Container>
                </div>
                <div className="notifications-content">
                    <Container>
                        <Row>
                            <Col>
                                <div className="loading-placeholder">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="notifications-page">
                <div className="notifications-hero">
                    <Container>
                        <Row>
                            <Col>
                                <h1>Notificaciones</h1>
                            </Col>
                        </Row>
                    </Container>
                </div>
                <div className="notifications-content">
                    <Container>
                        <Row>
                            <Col>
                                <div className="error-message">
                                    <p className="text-danger">{error}</p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-page">
            {/* Hero Section */}
            <div className="notifications-hero">
                <Container>
                    <Row>
                        <Col>
                            <h1>Notificaciones</h1>
                            <p className="hero-subtitle">Mantente al tanto de tus notificaciones</p>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Notifications Content */}
            <div className="notifications-content">
                <Container>
                    <Row>
                        <Col className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className="mb-0">Tus notificaciones</h3>
                            <button
                                className="btn btn-primary"
                                onClick={() => fetchNotifications(auth.token)}
                                disabled={loading}
                                style={{ fontSize: '1.2rem' }}
                            >
                                {loading ? 'Actualizando...' : '🔄 Actualizar'}
                            </button>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {notifications.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon">📭</div>
                                    <h3>No tienes notificaciones</h3>
                                    <p>Revisa más tarde para más notificaciones.</p>
                                </div>
                            ) : (
                                <div className="notifications-list">
                                    {notifications.map((notification) => (
                                        <Card
                                            key={notification.id}
                                            className={`notification-card mb-3 ${notification.read ? 'read' : 'unread'}`}
                                        >
                                            <Card.Body>
                                                <div className="notification-header">
                                                    <div className="notification-icon">
                                                        {getNotificationIcon(notification.type)}
                                                    </div>
                                                    <div className="notification-meta">
                                                        <div className="notification-title-row">
                                                            <h3 className="notification-title">
                                                                {notification.title}
                                                            </h3>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge
                                                                    bg={getNotificationBadgeVariant(notification.type)}
                                                                    className="notification-type-badge text-white"
                                                                >
                                                                    {getNotificationTypeTranslation(notification.type)}
                                                                </Badge>
                                                                {!notification.read && (
                                                                    <Badge bg="danger" className="unread-badge text-white">
                                                                        Nueva
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="notification-message">
                                                            {notification.message}
                                                        </p>
                                                        <div className="notification-footer">
                                                            <div className="d-flex align-items-center justify-content-between w-100">
                                                                <small className="text-muted">
                                                                    {formatDate(notification.createdAt)}
                                                                </small>
                                                                {!notification.read && (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-success"
                                                                        onClick={() => markNotificationAsRead(notification.id)}
                                                                        disabled={!!markingReadId}
                                                                    >
                                                                        {markingReadId === notification.id ? (
                                                                            <>
                                                                                <span className="notification-btn-spinner" aria-hidden="true" />
                                                                                Marcando...
                                                                            </>
                                                                        ) : (
                                                                            '✓ Marcar como leído'
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {/* {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                                                                <div className="notification-metadata">
                                                                    <small className="text-muted">
                                                                        Additional details available
                                                                    </small>
                                                                </div>
                                                            )} */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>

            {actionError && (
                <div
                    className="notification-error-overlay"
                    onClick={() => setActionError(null)}
                    role="presentation"
                >
                    <div
                        className="notification-error-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="notification-error-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="notification-error-title">No se pudo completar</h2>
                        <p>{actionError}</p>
                        <button
                            type="button"
                            className="notification-error-dialog__close"
                            onClick={() => setActionError(null)}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notifications;
